import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuCalendarDays, LuCircleCheck, LuClock, LuX } from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { Field, TextInput } from '../../components/form/FormControls';
import usePagedList from '../../hooks/usePagedList';
import { EmptyState, LoadingRows, SimplePager } from '../ProfilePage/components/ProfileUi';
import useSmoText from './smoText';
import { isJobInRange, jobDays } from './workerJobs';

const PAGE_SIZE = 20;
const COLUMNS = ['date', 'time', 'job', 'project', 'status'];
const HEADER_CELL = 'tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400';
const MUTED_CELL = 'tw-px-4 tw-py-2.5 tw-text-slate-700 dark:tw-text-slate-200';

/** Latest day first; the jobs of one day in clock order. */
const byLatestDay = (a, b) => jobDays(b).start.localeCompare(jobDays(a).start)
  || String(a.time || '').localeCompare(String(b.time || ''));

/**
 * Every calendar job (CalendarJobs) of a worker, finished ones included: the
 * same records the Operasyon Yönetimi calendar shows. The report has already
 * loaded them, so this window reads nothing and only views.
 */
const WorkerJobsModal = ({ worker, jobs, loading = false, initialStartDate = '', initialEndDate = '', onClose }) => {
  const { t, label, language, locale } = useSmoText();
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const shownJobs = useMemo(() => jobs
    .filter((job) => isJobInRange(job, startDate, endDate))
    .sort(byLatestDay), [jobs, startDate, endDate]);
  const { pageRows, currentPage, totalPages, goToPage } = usePagedList(shownJobs, {
    resetKey: `${startDate}|${endDate}`,
    initialPageSize: PAGE_SIZE,
  });
  const completedCount = shownJobs.filter((job) => job.status).length;

  const formatDay = (value) => {
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const dayText = (job) => {
    const { start, end } = jobDays(job);
    if (!start) return '-';
    return start === end ? formatDay(start) : `${formatDay(start)} – ${formatDay(end)}`;
  };
  const statusBadge = (job) => (
    <Badge tone={job.status ? 'success' : 'warning'}>
      {job.status ? <LuCircleCheck aria-hidden="true" className="tw-h-3 tw-w-3" /> : <LuClock aria-hidden="true" className="tw-h-3 tw-w-3" />}
      {job.status ? label('jobs.done') : label('jobs.open')}
    </Badge>
  );

  let body;
  if (loading) {
    body = <LoadingRows count={5} height="tw-h-12" label={label('jobs.loading')} />;
  } else if (jobs.length === 0) {
    body = <EmptyState icon={LuBriefcase} title={label('jobs.empty')} text={label('jobs.emptyHint')} />;
  } else {
    body = (
      <div className="tw-space-y-4">
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
          <Field label={label('jobs.startDate')} htmlFor="worker-jobs-start" className="tw-w-full sm:tw-w-48">
            <TextInput id="worker-jobs-start" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
          </Field>
          <Field label={label('jobs.endDate')} htmlFor="worker-jobs-end" className="tw-w-full sm:tw-w-48">
            <TextInput id="worker-jobs-end" type="date" value={endDate} min={startDate || undefined} onChange={(event) => setEndDate(event.target.value)} />
          </Field>
          {(startDate || endDate) && (
            <Button
              variant="ghost"
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
            >
              <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('jobs.clearDates')}
            </Button>
          )}
          <div className="tw-flex tw-flex-wrap tw-gap-1.5 sm:tw-ml-auto sm:tw-pb-2">
            <Badge tone="success">{label('jobs.completed', { count: completedCount })}</Badge>
            <Badge tone="warning">{label('jobs.ongoing', { count: shownJobs.length - completedCount })}</Badge>
          </div>
        </div>

        {shownJobs.length === 0 ? (
          <EmptyState compact icon={LuCalendarDays} title={label('jobs.noMatch')} text={label('jobs.noMatchHint')} />
        ) : (
          <>
            {/* Tablet ve masaüstü: tablo */}
            <div className="tw-hidden tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 md:tw-block dark:tw-border-white/10">
              <table className="tw-w-full tw-border-collapse tw-text-left tw-text-sm">
                <thead className="tw-bg-slate-50/90 dark:tw-bg-white/[0.03]">
                  <tr>
                    {COLUMNS.map((key) => <th key={key} scope="col" className={HEADER_CELL}>{label(`jobs.columns.${key}`)}</th>)}
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-slate-200/70 dark:tw-divide-white/[0.06]">
                  {pageRows.map((job) => (
                    <tr key={job.id} className="tw-transition-colors hover:tw-bg-slate-900/[0.02] dark:hover:tw-bg-white/[0.03]">
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5 tw-font-medium tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{dayText(job)}</td>
                      <td className={`${MUTED_CELL} tw-whitespace-nowrap tw-tabular-nums`}>{job.time || '-'}</td>
                      <td className={`${MUTED_CELL} tw-whitespace-normal tw-break-words`}>
                        <span className="tw-block tw-font-medium tw-text-slate-900 dark:tw-text-white">{job.title || '-'}</span>
                        {job.description && <small className="tw-text-slate-500 dark:tw-text-slate-400">{job.description}</small>}
                      </td>
                      <td className={`${MUTED_CELL} tw-whitespace-nowrap`}>{job.projectName || '-'}</td>
                      <td className="tw-whitespace-nowrap tw-px-4 tw-py-2.5">{statusBadge(job)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Telefon: kart listesi */}
            <ul className="tw-space-y-2 md:tw-hidden">
              {pageRows.map((job) => (
                <li key={job.id} className="tw-space-y-1.5 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                  <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                    <p className="tw-min-w-0 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{job.title || '-'}</p>
                    {statusBadge(job)}
                  </div>
                  {job.description && <p className="tw-break-words tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{job.description}</p>}
                  <p className="tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{[dayText(job), job.time, job.projectName].filter(Boolean).join(' · ')}</p>
                </li>
              ))}
            </ul>

            <SimplePager page={currentPage} pageCount={totalPages} onChange={goToPage} />
          </>
        )}
      </div>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title={label('jobs.title')}
      subtitle={`${worker.name} ${worker.surname}`.trim()}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30" size="xs">
          <LuBriefcase aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <span className="tw-mr-auto tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-600 max-sm:tw-hidden dark:tw-text-slate-300">
            {label('jobs.total', { count: shownJobs.length })}
          </span>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
        </>
      )}
    >
      {body}
    </Modal>
  );
};

WorkerJobsModal.propTypes = {
  worker: PropTypes.shape({ name: PropTypes.string, surname: PropTypes.string }).isRequired,
  jobs: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  initialStartDate: PropTypes.string,
  initialEndDate: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default WorkerJobsModal;
